/**
 * Repo-import modal (GIT2) — opened from the chat "import_repo" card during
 * discovery. Three ways in, stacked as organized sections: per-provider
 * (GitHub/GitLab) repo pickers with inline Connect buttons, a public-URL row,
 * and a .tar.gz upload. Every candidate repo row carries a green checkbox
 * toggle; toggling on/off POSTs/DELETEs the staged import source immediately,
 * and the full-width footer button ("Import N repositories") resumes the
 * pending request_repo_import tool with the outcome. Staged selections are
 * restored from GET git-import-source on open, so the OAuth connect
 * round-trip (or any reload) lands back with state intact.
 *
 * Self-contained fixed overlay (NOT PanelModal): discovery renders no main
 * pane, so the main-pane-scoped panel wrapper can't host it there.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import type { IconName } from '@molecule/app-icons'
import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex, Icon } from '@molecule/app-ui-react'

import { IMPORT_REPO_CONNECT_RETURN_KEY } from '../chat-custom-events.js'
import { API_BASE_URL } from '../config.js'
import { useIsMobile, useMediaQuery } from '../hooks/useMediaQuery.js'
import { importErrorMessage, looksLikeGzip } from '../utilities/import-error-copy.js'

type GitProvider = 'github' | 'gitlab'

const PROVIDER_LABELS: Record<GitProvider, string> = { github: 'GitHub', gitlab: 'GitLab' }
const PROVIDER_ICONS: Record<GitProvider, IconName> = { github: 'github', gitlab: 'gitlab' }

interface ProviderStatus {
  provider: GitProvider
  configured: boolean
  connected: boolean
}

interface RepoListing {
  fullName: string
  url: string
  private: boolean
  defaultBranch: string | null
  sizeKb: number | null
  updatedAt: string | null
  description: string | null
}

/** One staged selection (mirrors the server's ImportSource essentials). */
interface Selection {
  url: string
  repoName: string
  branch: string | null
}

/** Per-provider repo-list pagination state. */
interface RepoListState {
  repos: RepoListing[]
  page: number
  hasMore: boolean
  loading: boolean
  /** Listing failure for THIS provider (e.g. a token missing `read_api`). */
  error: string | null
}

const EMPTY_LIST: RepoListState = {
  repos: [],
  page: 0,
  hasMore: false,
  loading: false,
  error: null,
}

interface ImportRepoModalProps {
  projectId: string
  open: boolean
  onClose: () => void
  /** Called with the chat resume message after the import source is saved. */
  onDone: (message: string) => void
  /**
   * Called when staging a repo renamed the project (the server adopts the
   * first imported repo's name while the project is still 'Untitled') so the
   * host can reflect the new name without a refetch.
   */
  onProjectRenamed?: (name: string) => void
}

/**
 * The green selected-for-import checkbox. A row-level toggle rather than a
 * one-shot "Import" button: checked (green + check glyph) means the repo is
 * staged; toggling off unstages it.
 */
function SelectToggle({
  on,
  pending,
  molId,
}: {
  on: boolean
  pending: boolean
  molId: string
}): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      data-mol-id={molId}
      style={{
        width: 20,
        height: 20,
        borderRadius: 5,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: on
          ? '1px solid var(--color-success, #22a06b)'
          : '1px solid var(--color-border-secondary)',
        background: on ? 'var(--color-success, #22a06b)' : 'var(--color-surface)',
        color: '#fff',
        opacity: pending ? 0.6 : 1,
      }}
    >
      {on && <Icon name="check" size={13} />}
    </span>
  )
}

/**
 * The repo picker + public-URL + tarball-upload surface for importing
 * existing codebases (multi-select, multi-source).
 */
export function ImportRepoModal({
  projectId,
  open,
  onClose,
  onDone,
  onProjectRenamed,
}: ImportRepoModalProps): React.JSX.Element | null {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  // Phones/touch: 44px close target and 16px input font (iOS Safari zooms the
  // page when a focusable field below 16px receives focus).
  const isMobile = useIsMobile()
  const coarse = useMediaQuery('(pointer: coarse)')

  const [providers, setProviders] = useState<ProviderStatus[]>([])
  const [lists, setLists] = useState<Record<GitProvider, RepoListState>>({
    github: EMPTY_LIST,
    gitlab: EMPTY_LIST,
  })
  const [error, setError] = useState<string | null>(null)
  const [publicUrl, setPublicUrl] = useState('')
  const [branch, setBranch] = useState('')
  const [uploading, setUploading] = useState(false)
  // The url whose stage/unstage request is in flight (disables that toggle).
  const [pendingUrl, setPendingUrl] = useState<string | null>(null)
  // Staged selections ACCUMULATE (multi-repo import — each clones to
  // /workspace/<name>/); restored from the server on open.
  const [selected, setSelected] = useState<Selection[]>([])
  // Whether the choice is still open (before the first build); null until read.
  const [allowed, setAllowed] = useState<boolean | null>(null)
  // The staged list could not be read: nothing can be toggled until it can.
  const [listUnavailable, setListUnavailable] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // The staged selections and whether they can still change, from the server
  // — the ONE place the user can see what will be imported, so every failed
  // request re-reads it rather than guessing (R92-M5).
  const reloadSelected = useCallback(
    () =>
      http
        .get<{ importSources: Selection[]; allowed?: boolean }>(
          `/projects/${projectId}/git-import-source`,
        )
        .then((res) => {
          setSelected(res.data?.importSources ?? [])
          setAllowed(res.data?.allowed ?? true)
          setListUnavailable(false)
        })
        .catch(() => {
          setListUnavailable(true)
        }),
    [http, projectId],
  )

  // Load provider connection status + previously staged selections on open.
  useEffect(() => {
    if (!open) return
    setError(null)
    http
      .get<{ providers: ProviderStatus[] }>(`/projects/${projectId}/git-credentials`)
      .then((res) => setProviders(res.data?.providers ?? []))
      .catch(() => {
        // Guests / transient failures — the public-URL path still works.
        setProviders([])
      })
    void reloadSelected()
  }, [open, projectId, http, reloadSelected])

  // Close on Escape while open (parity with SettingsModal / the app's dialogs).
  useEffect(() => {
    if (!open) return undefined
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  // Load a page of repos for a connected provider.
  const loadRepos = useCallback(
    (provider: GitProvider, nextPage: number) => {
      setLists((prev) => ({ ...prev, [provider]: { ...prev[provider], loading: true } }))
      setError(null)
      http
        .get<{ repos: RepoListing[]; hasMore: boolean }>(
          `/projects/${projectId}/git-repos?provider=${provider}&page=${nextPage}`,
        )
        .then((res) => {
          setLists((prev) => ({
            ...prev,
            [provider]: {
              repos: nextPage === 1 ? res.data.repos : [...prev[provider].repos, ...res.data.repos],
              page: nextPage,
              hasMore: res.data.hasMore,
              loading: false,
              error: null,
            },
          }))
        })
        .catch((err: { response?: { data?: { error?: string } } }) => {
          // Keep the failure scoped to this provider's section (the other
          // sources stay usable) and offer a Reconnect there — a stale token
          // missing a newly-required scope is the common cause.
          const message =
            err.response?.data?.error ??
            t('importRepo.error.listFailed', undefined, {
              defaultValue: 'Could not load your repositories. Try again.',
            })
          setLists((prev) => ({
            ...prev,
            [provider]: { ...prev[provider], loading: false, error: message },
          }))
        })
    },
    [http, projectId, t],
  )

  // Auto-load page 1 for every connected provider (all sources at once).
  // The `error === null` gate matters: a failed load leaves page 0, and
  // without it this effect would retry the failing provider forever.
  useEffect(() => {
    if (!open) return
    for (const p of providers) {
      const list = lists[p.provider]
      if (p.connected && list.page === 0 && !list.loading && list.error === null) {
        loadRepos(p.provider, 1)
      }
    }
  }, [open, providers, lists, loadRepos])

  /** Stage a repo for import (POST); adds it to the selection on success. */
  const stage = useCallback(
    (url: string, chosenBranch: string | null, onStaged?: () => void) => {
      setPendingUrl(url)
      setError(null)
      http
        .post<{
          importSource: {
            url?: string
            repoName: string
            branch: string | null
            defaultBranch: string | null
          }
          projectName?: string
        }>(`/projects/${projectId}/git-import-source`, {
          url,
          ...(chosenBranch ? { branch: chosenBranch } : {}),
        })
        .then((res) => {
          const source = res.data.importSource
          setSelected((prev) => [
            ...prev,
            // The url AS STORED (trimmed, no trailing slash — R92-M2), so a
            // later unstage names the same source.
            {
              url: source.url ?? url,
              repoName: source.repoName,
              branch: source.branch ?? source.defaultBranch,
            },
          ])
          if (res.data.projectName) onProjectRenamed?.(res.data.projectName)
          onStaged?.()
        })
        .catch((err: { response?: { data?: { errorKey?: string; error?: string } } }) => {
          setError(importErrorMessage(err, t))
          // The server may hold the source anyway (a 502 after the first-build
          // gate, a 409 for one already there): show its list, not a guess.
          void reloadSelected()
        })
        .finally(() => setPendingUrl(null))
    },
    [http, projectId, t, onProjectRenamed, reloadSelected],
  )

  /** Unstage a previously staged repo (DELETE). */
  const unstage = useCallback(
    (url: string) => {
      setPendingUrl(url)
      setError(null)
      http
        .delete(`/projects/${projectId}/git-import-source?url=${encodeURIComponent(url)}`)
        .then(() => setSelected((prev) => prev.filter((s) => s.url !== url)))
        .catch((err: { response?: { data?: { errorKey?: string; error?: string } } }) => {
          setError(importErrorMessage(err, t))
          void reloadSelected()
        })
        .finally(() => setPendingUrl(null))
    },
    [http, projectId, t, reloadSelected],
  )

  /** Kick off the provider OAuth consent (connect or reconnect). */
  const connectProvider = useCallback(
    (provider: GitProvider) => {
      // Mark the departure so the Workspace reopens this modal when the OAuth
      // redirect lands back on the workspace.
      sessionStorage.setItem(IMPORT_REPO_CONNECT_RETURN_KEY, projectId)
      // Top-level nav so the API initiate route sees the session cookie (same
      // flow as the Pipeline panel's connect).
      window.location.href = `${API_BASE_URL}/projects/${projectId}/git-auth/${provider}`
    },
    [projectId],
  )

  const isSelected = useCallback((url: string) => selected.some((s) => s.url === url), [selected])

  const toggle = useCallback(
    (url: string) => {
      if (pendingUrl !== null || allowed === false || listUnavailable) return
      if (isSelected(url)) unstage(url)
      else stage(url, null)
    },
    [pendingUrl, allowed, listUnavailable, isSelected, stage, unstage],
  )

  /** Upload a `.tar.gz` of a codebase (no-git path); staged on success. */
  const uploadFile = useCallback(
    async (file: File) => {
      setError(null)
      // Checked here first: the server refuses anything else with 415 after
      // the whole file has been sent (R92-L9).
      if (!(await looksLikeGzip(file))) {
        setError(
          t('importRepo.error.notGzip', undefined, {
            defaultValue:
              'That file is not a .tar.gz archive. Compress your project with tar and try again.',
          }),
        )
        return
      }
      setUploading(true)
      setPendingUrl(`upload:${file.name}`)
      http
        .post<{ importSource: { repoName: string; branch: string | null }; projectName?: string }>(
          `/projects/${projectId}/git-import-upload`,
          file,
          // Percent-encoded: a header cannot carry characters above Latin-1,
          // and the browser refused the request for a non-Latin name (R92-M3).
          {
            headers: {
              'Content-Type': 'application/gzip',
              'X-Filename': encodeURIComponent(file.name),
            },
          },
        )
        .then((res) => {
          const source = res.data.importSource
          setSelected((prev) => [
            ...prev,
            { url: `upload:${source.repoName}`, repoName: source.repoName, branch: null },
          ])
          if (res.data.projectName) onProjectRenamed?.(res.data.projectName)
        })
        .catch((err: { response?: { data?: { errorKey?: string; error?: string } } }) => {
          setError(importErrorMessage(err, t))
          void reloadSelected()
        })
        .finally(() => {
          setUploading(false)
          setPendingUrl(null)
        })
    },
    [http, projectId, t, onProjectRenamed, reloadSelected],
  )

  if (!open) return null

  const visibleProviders = providers.filter((p) => p.configured || p.connected)
  const hasProviderSection = visibleProviders.length > 0

  // Selections that came from the URL/upload paths (no picker row to carry
  // their toggle) render as their own rows under the section that made them.
  const pickerUrls = new Set([...lists.github.repos, ...lists.gitlab.repos].map((r) => r.url))
  const uploadSelections = selected.filter((s) => s.url.startsWith('upload:'))
  const manualSelections = selected.filter(
    (s) => !s.url.startsWith('upload:') && !pickerUrls.has(s.url),
  )

  // The IDE's standard boxed text input (same treatment as the Deploy panel) —
  // NOT cm.input(), which is the floating-label underline style and renders as
  // a centered-text underline with an invisible placeholder when used bare.
  const inputStyle: React.CSSProperties = {
    height: isMobile ? 40 : 32,
    boxSizing: 'border-box',
    padding: '6px 10px',
    // 16px on phones/touch — iOS zooms any focusable field below 16px.
    fontSize: isMobile || coarse ? 16 : 13,
    borderRadius: 6,
    border: '1px solid var(--color-border-secondary)',
    background: 'var(--color-surface)',
    color: 'inherit',
    outline: 'none',
  }

  /** Thin "── or ──" rule separating the three import methods. */
  const orDivider = (
    <Flex align="center" gap="sm" aria-hidden>
      <span style={{ flex: 1, height: 1, background: 'var(--color-border-secondary)' }} />
      <span className={cm.cn(cm.textSize('xs'), cm.textMuted)}>
        {t('importRepo.or', undefined, { defaultValue: 'or' })}
      </span>
      <span style={{ flex: 1, height: 1, background: 'var(--color-border-secondary)' }} />
    </Flex>
  )

  /** A toggleable repo/selection row (whole row is the checkbox control). */
  const selectableRow = (opts: {
    key: string
    url: string
    title: string
    subtitle?: string | null
    molId: string
  }) => {
    const on = isSelected(opts.url)
    const pending = pendingUrl === opts.url
    return (
      // mol-bespoke-button: a CHECKBOX ROW (role="checkbox"), not a CTA —
      // title + subtitle on the left, a SelectToggle switch on the right,
      // filling the modal's width. The toggle carries the affordance.
      <button
        key={opts.key}
        type="button"
        role="checkbox"
        aria-checked={on}
        disabled={pendingUrl !== null && !pending}
        data-mol-id={opts.molId}
        onClick={() => toggle(opts.url)}
        className={cm.sp('p', 2)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: '1px solid transparent',
          borderRadius: 6,
          cursor: 'pointer',
          color: 'inherit',
        }}
      >
        <span style={{ minWidth: 0 }}>
          <span
            className={cm.cn(cm.textSize('sm'), cm.fontWeight('medium'))}
            style={{ display: 'block' }}
          >
            {opts.title}
          </span>
          {opts.subtitle && (
            <span
              className={cm.cn(cm.textSize('xs'), cm.textMuted)}
              style={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 380,
              }}
            >
              {opts.subtitle}
            </span>
          )}
        </span>
        <SelectToggle on={on} pending={pending} molId={`${opts.molId}-toggle`} />
      </button>
    )
  }

  const importLabel =
    selected.length === 0
      ? t('importRepo.importAtLeastOne', undefined, {
          defaultValue: 'Import at least 1 repository',
        })
      : selected.length === 1
        ? t('importRepo.importOne', undefined, { defaultValue: 'Import 1 repository' })
        : t(
            'importRepo.importMany',
            { count: String(selected.length) },
            { defaultValue: `Import ${selected.length} repositories` },
          )

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(4px)',
      }}
      data-mol-id="import-repo-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className={cm.cn(cm.surface, cm.sp('p', 6))}
        style={{
          borderRadius: 12,
          width: 'min(560px, 92vw)',
          // dvh, not vh: on phones vh ignores the browser chrome, letting the
          // dialog run under the toolbar; identical on desktop.
          maxHeight: '84dvh',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          // The title row + footer CTA stay pinned; only the body scrolls
          // (and with typical content it doesn't need to).
          overflow: 'hidden',
        }}
        data-mol-id="import-repo-modal"
      >
        <Flex justify="between" align="center">
          <h2 className={cm.cn(cm.textSize('lg'), cm.fontWeight('bold'))}>
            {t('importRepo.title', undefined, { defaultValue: 'Import your repository' })}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-mol-id="import-repo-close"
            aria-label={t('common.close', undefined, { defaultValue: 'Close' })}
            // 44px touch floor on phones (desktop keeps the compact size).
            style={isMobile ? { minWidth: 44, minHeight: 44 } : undefined}
          >
            <Icon name="x-mark" size={16} />
          </Button>
        </Flex>

        {/* Scrollable body — everything between the pinned title and footer. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          <p className={cm.cn(cm.textSize('sm'), cm.textMuted)}>
            {t('importRepo.subtitle', undefined, {
              defaultValue:
                'Your code is cloned into a private workspace on its own branch — your existing branches are never touched.',
            })}
          </p>

          {allowed === false && (
            <div
              role="status"
              data-mol-id="import-repo-locked"
              className={cm.cn(cm.textSize('sm'), 'text-on-surface-variant')}
              style={{ padding: '8px 0' }}
            >
              {t('importRepo.error.afterBoot', undefined, {
                defaultValue:
                  'This project has already started. Repositories can be imported only before its first build.',
              })}
            </div>
          )}
          {listUnavailable && (
            <div
              role="alert"
              data-mol-id="import-repo-list-unavailable"
              className={cm.cn(cm.textSize('sm'), 'text-error')}
              style={{ padding: '8px 0' }}
            >
              {t('importRepo.error.listUnavailable', undefined, {
                defaultValue: 'Could not read which repositories are selected. Close and reopen.',
              })}
            </div>
          )}
          {error && (
            <div
              className={cm.cn(cm.textSize('sm'), cm.sp('p', 3))}
              style={{ border: '1px solid var(--color-error, #e5484d)', borderRadius: 6 }}
              data-mol-id="import-repo-error"
            >
              {error}
            </div>
          )}

          {/* No provider app registered on this server at all — say why private
            repos won't work instead of silently omitting the connect buttons. */}
          {providers.length > 0 && !hasProviderSection && (
            <p
              className={cm.cn(cm.textSize('xs'), cm.textMuted)}
              data-mol-id="import-repo-no-oauth"
            >
              {t('importRepo.connectUnavailable', undefined, {
                defaultValue:
                  'Private repositories can’t be imported here — this server has no GitHub/GitLab app configured.',
              })}
            </p>
          )}

          {/* One section per provider: icon + name, inline Connect when not yet
            connected, and the (multi-select) repo list when connected — both
            sources stay visible and usable at the same time. */}
          {visibleProviders.map((p) => {
            const label = PROVIDER_LABELS[p.provider]
            const list = lists[p.provider]
            return (
              <Flex
                key={p.provider}
                direction="column"
                gap="xs"
                data-mol-id={`import-repo-section-${p.provider}`}
              >
                <Flex justify="between" align="center">
                  <Flex gap="xs" align="center">
                    <Icon name={PROVIDER_ICONS[p.provider]} size={16} />
                    <span className={cm.cn(cm.textSize('sm'), cm.fontWeight('medium'))}>
                      {label}
                    </span>
                  </Flex>
                  {!p.connected && (
                    <Button
                      variant="outline"
                      size="sm"
                      style={{ height: 32 }}
                      data-mol-id={`import-repo-connect-${p.provider}`}
                      onClick={() => connectProvider(p.provider)}
                    >
                      {t(
                        'importRepo.connect',
                        { provider: label },
                        { defaultValue: `Connect ${label}` },
                      )}
                    </Button>
                  )}
                </Flex>
                {p.connected && list.error !== null && (
                  <Flex justify="between" align="center" gap="sm">
                    <span className={cm.cn(cm.textSize('xs'), cm.textMuted)}>{list.error}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      style={{ height: 32, flexShrink: 0 }}
                      data-mol-id={`import-repo-reconnect-${p.provider}`}
                      onClick={() => connectProvider(p.provider)}
                    >
                      {t('git.reconnect', undefined, { defaultValue: 'Reconnect' })}
                    </Button>
                  </Flex>
                )}
                {p.connected &&
                  list.error === null &&
                  (list.loading && list.repos.length === 0 ? (
                    <p className={cm.cn(cm.textSize('sm'), cm.textMuted)}>
                      {t('importRepo.loading', undefined, {
                        defaultValue: 'Loading repositories…',
                      })}
                    </p>
                  ) : (
                    <Flex direction="column" style={{ maxHeight: 150, overflowY: 'auto' }}>
                      {list.repos.map((repo) =>
                        selectableRow({
                          key: repo.url,
                          url: repo.url,
                          title:
                            repo.fullName +
                            (repo.private
                              ? ` · ${t('importRepo.private', undefined, { defaultValue: 'private' })}`
                              : ''),
                          subtitle: repo.description,
                          molId: `import-repo-pick-${repo.fullName}`,
                        }),
                      )}
                      {list.hasMore && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={list.loading}
                          data-mol-id={`import-repo-more-${p.provider}`}
                          onClick={() => loadRepos(p.provider, list.page + 1)}
                        >
                          {t('importRepo.more', undefined, { defaultValue: 'Load more' })}
                        </Button>
                      )}
                    </Flex>
                  ))}
              </Flex>
            )
          })}

          {/* Public URL — works without any connection. */}
          {hasProviderSection && orDivider}
          <Flex direction="column" gap="xs">
            <label
              className={cm.cn(cm.textSize('sm'), cm.fontWeight('medium'))}
              htmlFor="import-repo-url"
            >
              {t('importRepo.publicUrl', undefined, {
                defaultValue: 'Paste a public repository URL',
              })}
            </label>
            {/* Wraps only when the row can't fit (phones): the URL input takes
                its own full-width line; branch + Add share the next one. */}
            <Flex gap="sm" wrap="wrap">
              <input
                id="import-repo-url"
                type="text"
                value={publicUrl}
                onChange={(e) => setPublicUrl(e.target.value)}
                placeholder="https://github.com/owner/repo"
                style={{ ...inputStyle, flex: '1 1 220px', minWidth: 0 }}
                data-mol-id="import-repo-url"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && publicUrl.trim() && pendingUrl === null) {
                    stage(publicUrl.trim(), branch.trim() || null, () => {
                      setPublicUrl('')
                      setBranch('')
                    })
                  }
                }}
              />
              <input
                id="import-repo-branch"
                type="text"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                placeholder={t('importRepo.branchPlaceholder', undefined, {
                  defaultValue: 'branch (optional)',
                })}
                style={{ ...inputStyle, width: 150 }}
                data-mol-id="import-repo-branch"
              />
              <Button
                variant="solid"
                size="sm"
                style={{ height: isMobile ? 40 : 32 }}
                disabled={
                  !publicUrl.trim() || pendingUrl !== null || allowed === false || listUnavailable
                }
                data-mol-id="import-repo-url-submit"
                onClick={() =>
                  stage(publicUrl.trim(), branch.trim() || null, () => {
                    setPublicUrl('')
                    setBranch('')
                  })
                }
              >
                {t('importRepo.add', undefined, { defaultValue: 'Add' })}
              </Button>
            </Flex>
            {manualSelections.length > 0 && (
              <Flex direction="column">
                {manualSelections.map((sel) =>
                  selectableRow({
                    key: sel.url,
                    url: sel.url,
                    title: sel.repoName + (sel.branch ? ` (${sel.branch})` : ''),
                    subtitle: sel.url,
                    molId: `import-repo-manual-${sel.repoName}`,
                  }),
                )}
              </Flex>
            )}
          </Flex>

          {/* Tarball upload — no-git path (not in any repo). The native file
            input is hidden; a real Button (right-aligned) triggers it. */}
          {orDivider}
          <Flex direction="column" gap="xs">
            <Flex justify="between" align="center">
              <span className={cm.cn(cm.textSize('sm'), cm.fontWeight('medium'))}>
                {t('importRepo.uploadLabel', undefined, {
                  defaultValue: 'Upload a .tar.gz of your codebase',
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                style={{ height: 32 }}
                disabled={pendingUrl !== null || allowed === false || listUnavailable}
                data-mol-id="import-repo-file-button"
                onClick={() => fileInputRef.current?.click()}
              >
                <Flex gap="xs" align="center">
                  <Icon name="upload" size={14} />
                  {uploading
                    ? t('importRepo.uploading', undefined, { defaultValue: 'Uploading…' })
                    : t('importRepo.uploadButton', undefined, { defaultValue: 'Choose file…' })}
                </Flex>
              </Button>
            </Flex>
            <input
              ref={fileInputRef}
              id="import-repo-file"
              type="file"
              accept=".tar.gz,.tgz,application/gzip"
              data-mol-id="import-repo-file"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) uploadFile(file)
                e.target.value = ''
              }}
            />
            {uploadSelections.length > 0 && (
              <Flex direction="column">
                {uploadSelections.map((sel) =>
                  selectableRow({
                    key: sel.url,
                    url: sel.url,
                    title: sel.repoName,
                    molId: `import-repo-upload-${sel.repoName}`,
                  }),
                )}
              </Flex>
            )}
          </Flex>
        </div>

        {/* Footer CTA — disabled (with the reason) until something is staged.
            minHeight + flexShrink pin the height: as a flex-column child it
            would otherwise compress below its set height when the modal body
            scrolls. */}
        <Button
          variant="solid"
          size="sm"
          style={{
            height: 40,
            minHeight: 40,
            flexShrink: 0,
            width: '100%',
            padding: '10px 16px',
          }}
          disabled={
            selected.length === 0 || pendingUrl !== null || allowed === false || listUnavailable
          }
          data-mol-id="import-repo-done"
          onClick={() =>
            onDone(
              t(
                'importRepo.resumeMessage',
                { label: selected.map((sel) => sel.repoName).join(', '), branch: '' },
                {
                  defaultValue: `I've selected my ${selected.length > 1 ? 'repositories' : 'repository'} to import: ${selected
                    .map((sel) => sel.repoName + (sel.branch ? ` (${sel.branch})` : ''))
                    .join(', ')}. Please continue.`,
                },
              ),
            )
          }
        >
          {importLabel}
        </Button>
      </div>
    </div>
  )
}
